import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { useTenantStore } from '../../../stores/tenant.store';
import { useUiStore } from '../../../stores/ui.store';
import { evaluationStandardApi } from '../../../lib/api/evaluation-standard.api';
import {
  socraticApi,
  socraticReason,
  type SocraticSessionDto,
  type SocraticCoachingDto,
  type PracticeTrajectoryDto,
} from '../../../lib/api/socratic.api';
import { SocraticGrowthPanel } from './SocraticGrowthPanel';

type Phase = 'pick' | 'dialogue' | 'coaching';

// The self-study PRACTICE experience (Frente B): pick an approved standard with a
// question bank, dialogue against a random root question, get COACHING at the end.
// No grade, no teacher. Shared by the staff console (/dialogo-socratico) and the
// student portal (portal-v2/practica-socratica).
export function SocraticPracticeExperience(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const uiLang = useUiStore((s) => s.language);
  const locale: 'es' | 'en' = uiLang === 'en' ? 'en' : 'es';

  const [phase, setPhase] = useState<Phase>('pick');
  const [standards, setStandards] = useState<
    { id: string; title: string; questions: string[] }[] | null
  >(null);
  const [standardId, setStandardId] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [session, setSession] = useState<SocraticSessionDto | null>(null);
  const [coaching, setCoaching] = useState<SocraticCoachingDto | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trajectory, setTrajectory] = useState<PracticeTrajectoryDto | null>(null);

  const loadTrajectory = () => {
    if (!currentTenantId) return;
    socraticApi
      .getTrajectory({ tenantId: currentTenantId })
      .then((r) => setTrajectory(r.trajectory))
      .catch(() => {
        /* progress is optional — never block practice */
      });
  };

  useEffect(() => {
    loadTrajectory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    evaluationStandardApi
      .list({ tenantId: currentTenantId })
      .then((r) => {
        if (!alive) return;
        const withBank = r.standards
          .filter((s) => s.status === 'approved' && (s.practiceRootQuestions?.length ?? 0) > 0)
          .map((s) => ({ id: s.id, title: s.title, questions: s.practiceRootQuestions ?? [] }));
        setStandards(withBank);
        setStandardId((prev) => prev || withBank[0]?.id || '');
      })
      .catch(() => {
        if (alive) setStandards([]);
      });
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  const pendingQuestion = useMemo(() => {
    if (!session) return null;
    const last = session.turns[session.turns.length - 1];
    return last && last.answer === null ? last : null;
  }, [session]);

  const start = async () => {
    if (!currentTenantId || !standardId) return;
    setBusy(true);
    setError(null);
    try {
      const { session: s, remainingToday } = await socraticApi.startPractice({
        tenantId: currentTenantId,
        standardId,
        questionIndex,
        locale,
      });
      setSession(s);
      setRemaining(remainingToday);
      setCoaching(null);
      setAnswer('');
      setPhase('dialogue');
    } catch (e) {
      setError(
        socraticReason(e) === 'socratic.practice_quota_reached'
          ? t('socraticPractice.quotaReached')
          : t('socraticPractice.startError'),
      );
    } finally {
      setBusy(false);
    }
  };

  const runCoaching = async (sessionId: string) => {
    if (!currentTenantId) return;
    setBusy(true);
    try {
      const { coaching: c } = await socraticApi.coachPractice({
        tenantId: currentTenantId,
        sessionId,
      });
      setCoaching(c);
      setPhase('coaching');
      loadTrajectory(); // the just-scored session updates the trajectory
    } catch {
      setError(t('socraticPractice.coachError'));
    } finally {
      setBusy(false);
    }
  };

  const submitAnswer = async () => {
    if (!currentTenantId || !session || !answer.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { session: s, closed } = await socraticApi.advance({
        tenantId: currentTenantId,
        sessionId: session.id,
        answer: answer.trim(),
      });
      setSession(s);
      setAnswer('');
      if (closed) await runCoaching(s.id);
    } catch {
      setError(t('socraticPractice.answerError'));
    } finally {
      setBusy(false);
    }
  };

  const finishNow = async () => {
    if (!currentTenantId || !session) return;
    setBusy(true);
    setError(null);
    try {
      let sessionId = session.id;
      if (session.status === 'in_progress') {
        const { session: s } = await socraticApi.close({
          tenantId: currentTenantId,
          sessionId: session.id,
        });
        sessionId = s.id;
      }
      await runCoaching(sessionId);
    } catch {
      setError(t('socraticPractice.coachError'));
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setSession(null);
    setCoaching(null);
    setAnswer('');
    setError(null);
    setPhase('pick');
  };

  return (
    <Box>
      {(phase === 'pick' || phase === 'coaching') && (
        <SocraticGrowthPanel
          trajectory={trajectory}
          topicTitles={Object.fromEntries((standards ?? []).map((s) => [s.id, s.title]))}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {phase === 'pick' && (
        <PickPanel
          standards={standards}
          standardId={standardId}
          questionIndex={questionIndex}
          onStandard={(id) => {
            setStandardId(id);
            setQuestionIndex(0);
          }}
          onQuestion={setQuestionIndex}
          onStart={start}
          busy={busy}
        />
      )}

      {phase === 'dialogue' && session && (
        <DialoguePanel
          session={session}
          pendingQuestion={pendingQuestion}
          answer={answer}
          onAnswer={setAnswer}
          onSubmit={submitAnswer}
          onFinish={finishNow}
          remaining={remaining}
          busy={busy}
        />
      )}

      {phase === 'coaching' && coaching && (
        <CoachingPanel coaching={coaching} remaining={remaining} onRestart={restart} />
      )}
    </Box>
  );
}

function PickPanel({
  standards,
  standardId,
  questionIndex,
  onStandard,
  onQuestion,
  onStart,
  busy,
}: {
  standards: { id: string; title: string; questions: string[] }[] | null;
  standardId: string;
  questionIndex: number;
  onStandard: (id: string) => void;
  onQuestion: (i: number) => void;
  onStart: () => void;
  busy: boolean;
}): JSX.Element {
  const { t } = useTranslation('sis');
  if (standards === null) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (standards.length === 0) {
    return (
      <Alert severity="info" icon={<ForumRoundedIcon />}>
        {t('socraticPractice.noStandards')}
      </Alert>
    );
  }
  const selected = standards.find((s) => s.id === standardId) ?? null;
  const questions = selected?.questions ?? [];
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel>{t('socraticPractice.standard')}</InputLabel>
          <Select
            label={t('socraticPractice.standard')}
            value={standardId}
            onChange={(e) => onStandard(e.target.value)}
          >
            {standards.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {questions.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('socraticPractice.pickQuestion', { n: questions.length })}
            </Typography>
            <Stack spacing={1}>
              {questions.map((q, i) => {
                const active = i === questionIndex;
                return (
                  <Paper
                    key={i}
                    variant="outlined"
                    onClick={() => onQuestion(i)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      borderColor: active ? 'primary.main' : 'divider',
                      borderWidth: active ? 2 : 1,
                      bgcolor: (th) =>
                        active ? alpha(th.palette.primary.main, 0.08) : 'transparent',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" label={i + 1} color={active ? 'primary' : 'default'} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {q}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('socraticPractice.pickQuestionHint')}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ForumRoundedIcon />}
          onClick={onStart}
          disabled={busy || !standardId}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('socraticPractice.start')}
        </Button>
      </Stack>
    </Paper>
  );
}

function DialoguePanel({
  session,
  pendingQuestion,
  answer,
  onAnswer,
  onSubmit,
  onFinish,
  remaining,
  busy,
}: {
  session: SocraticSessionDto;
  pendingQuestion: SocraticSessionDto['turns'][number] | null;
  answer: string;
  onAnswer: (v: string) => void;
  onSubmit: () => void;
  onFinish: () => void;
  remaining: number | null;
  busy: boolean;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {t('socraticPractice.dialogueTitle')}
        </Typography>
        {remaining !== null && (
          <Chip
            size="small"
            variant="outlined"
            label={t('socraticPractice.remaining', { n: remaining })}
          />
        )}
      </Stack>

      {session.turns.map((turn) => (
        <Box key={turn.index} sx={{ mb: 2 }}>
          <Typography variant="caption" color="primary" fontWeight={700}>
            {t('socraticPractice.mentor')}
          </Typography>
          <Typography variant="body2" sx={{ mb: turn.answer !== null ? 1 : 0 }}>
            {turn.question}
          </Typography>
          {turn.answer !== null && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t('socraticPractice.you')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {turn.answer}
              </Typography>
            </>
          )}
        </Box>
      ))}

      {pendingQuestion && (
        <>
          <Divider sx={{ my: 2 }} />
          <TextField
            multiline
            minRows={3}
            fullWidth
            value={answer}
            onChange={(e) => onAnswer(e.target.value)}
            placeholder={t('socraticPractice.answerPlaceholder')}
            disabled={busy}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              startIcon={
                busy ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />
              }
              onClick={onSubmit}
              disabled={busy || !answer.trim()}
            >
              {t('socraticPractice.send')}
            </Button>
            <Button variant="text" onClick={onFinish} disabled={busy}>
              {t('socraticPractice.finish')}
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  );
}

function CoachingPanel({
  coaching,
  remaining,
  onRestart,
}: {
  coaching: SocraticCoachingDto;
  remaining: number | null;
  onRestart: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <AutoAwesomeRoundedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {t('socraticPractice.coachingTitle')}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
        {coaching.prose}
      </Typography>
      {coaching.suggestions.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t('socraticPractice.suggestions')}
          </Typography>
          <Stack component="ul" sx={{ pl: 2, mt: 0.5, mb: 2 }}>
            {coaching.suggestions.map((s, i) => (
              <Typography component="li" variant="body2" key={i} sx={{ mb: 0.5 }}>
                {s}
              </Typography>
            ))}
          </Stack>
        </>
      )}
      <Button
        variant="contained"
        startIcon={<ReplayRoundedIcon />}
        onClick={onRestart}
        disabled={remaining !== null && remaining <= 0}
      >
        {t('socraticPractice.again')}
      </Button>
      {remaining !== null && remaining <= 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('socraticPractice.quotaReached')}
        </Typography>
      )}
    </Paper>
  );
}
